import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Vault, GraduationCap, Home, BookOpenCheck } from "lucide-react";
import { SectionHeader } from "./SovereignPace";

export const BenefitsVault = () => {
    const [snap, setSnap] = useState(null);

    useEffect(() => {
        endpoints.benefitsSnapshot().then((r) => setSnap(r.data)).catch(() => {});
    }, []);

    if (!snap) return <SectionSkeleton />;

    return (
        <section
            id="benefits-vault"
            data-testid="benefits-vault"
            className="relative py-20 border-b border-zinc-900"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-8">
                <SectionHeader
                    eyebrow="MODULE · 002"
                    title="The Benefits Vault"
                    subtitle={`Real-time ledger · GI Bill · TA · CA · VA Loan signals · ${snap.regulation_ref}`}
                />

                <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-10">
                    <CapCard
                        testId="vault-ta"
                        icon={GraduationCap}
                        title="Tuition Assistance"
                        cap={snap.tuition_assistance.cap}
                        used={snap.tuition_assistance.used}
                        remaining={snap.tuition_assistance.remaining}
                        sub={`$${snap.tuition_assistance.per_credit_hour}/credit hour · AR 621-5`}
                    />
                    <CapCard
                        testId="vault-ca"
                        icon={BookOpenCheck}
                        title="Credentialing Assistance"
                        cap={snap.credentialing_assistance.cap}
                        used={snap.credentialing_assistance.used}
                        remaining={snap.credentialing_assistance.remaining}
                        sub="$2K annual cap · COOL aligned"
                    />
                    <div
                        data-testid="vault-gi-bill"
                        className="col-span-12 lg:col-span-4 border border-zinc-800/80 bg-zinc-950/60 p-6 relative corner-bracket"
                    >
                        <div className="flex items-center gap-2">
                            <Vault className="w-4 h-4 text-gold-300" />
                            <span className="label-tactical">CHAPTER 33 · POST-9/11</span>
                        </div>
                        <h3 className="text-xl font-black uppercase tracking-tight text-white mt-2">
                            GI Bill
                        </h3>
                        <div className="mt-5 space-y-3">
                            <Row
                                label="Monthly BAH (avg)"
                                value={`$${snap.gi_bill.monthly_bah_avg.toLocaleString()}`}
                            />
                            <Row
                                label="Books Stipend (annual)"
                                value={`$${snap.gi_bill.books_stipend_annual.toLocaleString()}`}
                            />
                            <Row label="Eligibility" value="36 months / 100%" />
                        </div>
                    </div>

                    <div data-testid="vault-va-loan" className="col-span-12 lg:col-span-7 border border-zinc-800/80 bg-zinc-950/60 p-6 relative">
                        <div className="flex items-center gap-2">
                            <Home className="w-4 h-4 text-gold-300" />
                            <span className="label-tactical">VA LOAN · SIGNAL</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <SignalBlock
                                label="2026 Loan Limit"
                                value={`$${snap.va_loan.loan_limit_2026.toLocaleString()}`}
                            />
                            <SignalBlock
                                label="Funding Fee · 1st Use"
                                value={`${snap.va_loan.funding_fee_first_use_pct.toFixed(2)}%`}
                            />
                            <SignalBlock
                                label="Signal"
                                value={snap.va_loan.signal}
                                accent
                            />
                        </div>
                    </div>

                    <div data-testid="vault-skillbridge" className="col-span-12 lg:col-span-5 border border-zinc-800/80 bg-zinc-950/60 p-6">
                        <div className="label-tactical">SKILLBRIDGE · TERMINAL LEAVE</div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <SignalBlock
                                label="Max SkillBridge"
                                value={`${snap.skillbridge.max_duration_days}d`}
                            />
                            <SignalBlock
                                label="Max Terminal Leave"
                                value={`${snap.skillbridge.terminal_leave_max_days}d`}
                            />
                        </div>
                        <p className="text-zinc-400 text-xs mt-4 leading-relaxed">
                            DoDI 1322.29 governs SkillBridge eligibility. Stack with terminal leave for
                            up to 240 days of paid pre-transition operations.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

const CapCard = ({ testId, icon: Icon, title, cap, used, remaining, sub }) => {
    const pct = cap > 0 ? Math.round((used / cap) * 100) : 0;
    return (
        <div
            data-testid={testId}
            className="col-span-12 sm:col-span-6 lg:col-span-4 border border-zinc-800/80 bg-zinc-950/60 p-6 relative"
        >
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-gold-300" />
                <span className="label-tactical">{title.toUpperCase()}</span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
                <div className="text-4xl font-black text-white tracking-tight">
                    ${remaining.toLocaleString()}
                </div>
                <div className="badge-tier text-zinc-500">REMAINING</div>
            </div>
            <div className="mt-3 text-xs text-zinc-400">
                Used <span className="text-white font-bold">${used.toLocaleString()}</span> /{" "}
                <span className="text-gold-300">${cap.toLocaleString()} CAP</span>
            </div>
            <div className="mt-3 h-1.5 bg-zinc-900 border border-zinc-800">
                <div className="h-full bg-gold-300" style={{ width: `${pct}%` }} />
            </div>
            <div className="badge-tier text-zinc-500 mt-3">{sub}</div>
        </div>
    );
};

const Row = ({ label, value }) => (
    <div className="flex items-center justify-between border-b border-zinc-900 pb-2">
        <span className="text-xs uppercase tracking-[0.18em] text-zinc-400">{label}</span>
        <span className="text-sm font-bold text-white">{value}</span>
    </div>
);

const SignalBlock = ({ label, value, accent }) => (
    <div className="border border-zinc-800/80 px-4 py-3">
        <div className="badge-tier text-zinc-500">{label.toUpperCase()}</div>
        <div
            className={`text-xl font-black mt-1 ${
                accent ? "text-emerald-400" : "text-white"
            }`}
        >
            {value}
        </div>
    </div>
);

const SectionSkeleton = () => (
    <section className="py-20 border-b border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8">
            <div className="text-zinc-500 text-sm">Loading benefits ledger...</div>
        </div>
    </section>
);

export default BenefitsVault;
